// Injectable FastAPI /api/admin/* client. Apps supply baseUrl, fetch, and
// optional auth/trace headers — no DOM or Next imports here.

import type {
  ModalPipeline,
  FreqResponseRequest,
  FreqResponseResponse,
  RT60Request,
  RT60Response,
  ModalRequest,
  ModalResponse,
  ModalVolumeRequest,
  ModalVolumeResponse,
  ModalVelocityRequest,
  ModalVelocityResponse,
  ModalVolumeSweepRequest,
  ModalVolumeSweepResponse,
  RaytraceRequest,
  RaytraceResponse,
  SpeakerSweepRequest,
  AdminSpeakerSweepResponse,
  SoundstageRequest,
  SoundstageResponse,
  OptimizeListenerRequest,
  OptimizeSpeakersRequest,
  OptimizeJointRequest,
  OptimizeResponse,
  MaterialsResponse,
  AdminProduct,
  Scene,
  JobAction,
  JobOrigin,
  JobSubmitRequest,
  JobSubmitResponse,
  JobPollResponse,
  JobAppliedResponse,
  RoomTreatmentRequest,
  RoomTreatmentResponse,
  AdminEvaluateRequest,
  AdminEvaluateResponse,
  AnalyzeSceneRequest,
  AnalyzeSceneResponse,
  PanelAbsorptionRequest,
  PanelAbsorptionResponse,
  PorousAbsorberRequest,
  PorousAbsorberResponse,
} from '@roomtreatment/core';
import { AdminApiError, adminErrorFromResponse } from './errors';
import { normalizeMaterialsResponse } from './normalizeMaterials';

export type FetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<Response>;

export interface AdminApiOptions {
  /** Prefix for every path. Empty / omitted = relative same-origin paths. */
  baseUrl?: string;
  fetch?: FetchLike;
  credentials?: RequestCredentials;
  /** Extra headers merged into every request (anon id, auth, etc.). */
  getExtraHeaders?: () => Record<string, string>;
  /** Mint a short correlation id for traced endpoints. */
  newTraceId?: () => string;
  /** Optional trace logger (web stamps browser console). */
  onTrace?: (traceId: string, label: string, fields: Record<string, unknown>) => void;
  traceHeader?: string;
  /** How many queued physics jobs (charts, fields, `analyze_scene`) this
   *  client keeps active at once, each from submit until it finishes.
   *  Defaults to `DEFAULT_MAX_CONCURRENT_QUEUED_JOBS`, the backend's
   *  per-owner cap; see `createQueuedJobLane`. */
  maxConcurrentQueuedJobs?: number;
}

/** The backend refuses a 4th concurrently active job per owner
 *  (`OWNER_ACTIVE_JOB_LIMIT` in backend/db.py, `owner_busy` 429). Kept equal
 *  to it by apiClient.test.ts, which reads the backend default. */
export const DEFAULT_MAX_CONCURRENT_QUEUED_JOBS = 3;

/** FIFO counting semaphore for queued physics jobs.
 *
 *  Why it exists: when a solve lands, the viz hooks ask for every chart and
 *  field at once -- the visible layers plus their silent before/after
 *  prefetches. On 2026-09-12 that was nine `POST /api/admin/jobs` in the
 *  same second; the server's per-owner cap took three and answered the
 *  other six with `owner_busy`, which nothing retried, so those layers
 *  simply never loaded (and each refusal still cost a full enqueue
 *  transaction). Holding a slot from submit to terminal state keeps this
 *  client at the cap instead of past it; waiters start in call order the
 *  moment a slot frees. The returned release is idempotent. */
function createQueuedJobLane(size: number): { acquire: () => Promise<() => void> } {
  let free = Math.max(1, Math.floor(size));
  const waiters: Array<() => void> = [];
  const handOff = () => {
    const next = waiters.shift();
    if (next) next();
    else free += 1;
  };
  return {
    async acquire() {
      if (free > 0) free -= 1;
      else await new Promise<void>((resolve) => waiters.push(resolve));
      let released = false;
      return () => {
        if (released) return;
        released = true;
        handOff();
      };
    },
  };
}

export interface AdminApi {
  materials: () => Promise<MaterialsResponse>;
  products: () => Promise<{ products: AdminProduct[] } | AdminProduct[]>;
  freqResponse: (req: FreqResponseRequest, pipeline?: ModalPipeline) => Promise<FreqResponseResponse>;
  rt60: (req: RT60Request, pipeline?: ModalPipeline) => Promise<RT60Response>;
  modal: (req: ModalRequest, pipeline?: ModalPipeline) => Promise<ModalResponse>;
  modalVolume: (req: ModalVolumeRequest, pipeline?: ModalPipeline) => Promise<ModalVolumeResponse>;
  modalVolumeSweep: (
    req: ModalVolumeSweepRequest,
    pipeline?: ModalPipeline,
  ) => Promise<ModalVolumeSweepResponse>;
  /** Band-cumulative particle-velocity field: one grid, no frequency axis. */
  modalVelocity: (
    req: ModalVelocityRequest,
    pipeline?: ModalPipeline,
  ) => Promise<ModalVelocityResponse>;
  raytrace: (req: RaytraceRequest) => Promise<RaytraceResponse>;
  panelAbsorption: (req: PanelAbsorptionRequest) => Promise<PanelAbsorptionResponse>;
  porousAbsorber: (req: PorousAbsorberRequest) => Promise<PorousAbsorberResponse>;
  speakerSweep: (req: SpeakerSweepRequest) => Promise<AdminSpeakerSweepResponse>;
  soundstage: (req: SoundstageRequest) => Promise<SoundstageResponse>;
  optimizeListener: (
    req: OptimizeListenerRequest,
    pipeline?: ModalPipeline,
    traceId?: string,
  ) => Promise<OptimizeResponse>;
  optimizeSpeakers: (
    req: OptimizeSpeakersRequest,
    pipeline?: ModalPipeline,
    traceId?: string,
  ) => Promise<OptimizeResponse>;
  optimizeJoint: (
    req: OptimizeJointRequest,
    pipeline?: ModalPipeline,
    traceId?: string,
  ) => Promise<OptimizeResponse>;
  optimizeRoomTreatment: (
    req: RoomTreatmentRequest,
    pipeline?: ModalPipeline,
    traceId?: string,
  ) => Promise<RoomTreatmentResponse>;
  evaluate: (req: AdminEvaluateRequest) => Promise<AdminEvaluateResponse>;
  /** Job pipeline W2 (2026-09-06), §3.5 "compute-everything": ONE queued
   *  job (`kind: 'analyze_scene'`) that returns freq/rt60/soundstage inline
   *  (same shape `evaluate` already returns) plus the four viz layers —
   *  modal/raytrace inline, volume/velocity as artifact references (see
   *  `fetchArtifact`). Replaces the guest three-job fan-out and the
   *  standalone per-layer viz calls `useModalViz` used to submit for the
   *  "after" (current-scene) slot. */
  analyzeScene: (req: AnalyzeSceneRequest, origin?: JobOrigin) => Promise<AnalyzeSceneResponse>;
  /** `GET /api/artifacts/<sha256>` — bulk viz bytes referenced by an
   *  `ArtifactRef` (`{ artifact, bytes, dtype, shape, encoding: 'gzip' }`).
   *  The body is ALWAYS a gzip stream regardless of `Content-Encoding`
   *  (decompressed here via `DecompressionStream`, never left to the
   *  browser) — decode once and get back the raw uncompressed bytes, ready
   *  to reinterpret as `Uint8Array` (dtype `'uint8'`) or `Float32Array`
   *  (dtype `'float32'`, `new Float32Array(bytes.buffer, bytes.byteOffset,
   *  bytes.byteLength >> 2)`). Read-through an in-memory LRU (~8 entries,
   *  same sha never refetched this session) since a sha is immutable by
   *  construction (`Cache-Control: public, max-age=31536000, immutable` on
   *  the response). A 404 means the artifact was garbage-collected —
   *  throws `AdminApiError` with `status === 404`; callers must treat that
   *  as "this layer is missing, recompute on next Compute", never as a
   *  hard error (see `docs/superpowers/specs/2026-09-06-job-pipeline-design.md`
   *  §3.2). Throws a plain `Error` if the runtime has no
   *  `DecompressionStream` (should not happen in a target browser). */
  fetchArtifact: (sha: string, signal?: AbortSignal) => Promise<Uint8Array>;
  /** `origin` (job pipeline W3, §3.1) rides in the request body alongside
   *  `scene`/`action` — see `JobOrigin`'s own doc comment. Optional: a
   *  caller with no notion of Designs (product-catalog tooling, tests) may
   *  omit it, and the server simply has nothing to echo back. */
  submitJob: (
    scene: Scene,
    action: JobAction,
    pipeline?: ModalPipeline,
    traceId?: string,
    origin?: JobOrigin,
  ) => Promise<JobSubmitResponse>;
  /** The onboarding freebie door — `POST /api/onboarding/placement`
   *  (`backend/routers/jobs.py`). Same `{ scene, action }` request body and
   *  the same `JobSubmitResponse` shape as `submitJob`, but spends no
   *  pooled evaluation (`eval_metered=False`): it claims the caller's ONE
   *  free onboarding solve instead. Intended for `action.kind ===
   *  'solve_room'` only — the backend 400s any other kind through this
   *  door. A `409` (`AdminApiError.status === 409`, detail `'freebie_used'`)
   *  means the owner already spent their one free solve; this method does
   *  NOT retry through the metered door itself — the caller catches the 409
   *  and resubmits the identical action via `submitJob`
   *  (docs/plans/2026-09-02-unified-room-solve-plan.md §3.6-3.7). */
  submitOnboardingSolve: (
    scene: Scene,
    action: JobAction,
    pipeline?: ModalPipeline,
    traceId?: string,
  ) => Promise<JobSubmitResponse>;
  pollJob: (jobId: string, traceId?: string, timeoutMs?: number) => Promise<JobPollResponse>;
  cancelJob: (jobId: string, traceId?: string) => Promise<{ ok?: boolean }>;
  /** Opt an in-flight job into a "your room is ready" email — see
   *  backend/routers/jobs.py's admin_notify_job. Account-only: the address
   *  is the signed-in caller's, not a request field. `roomToken` (§3.11
   *  step 2, docs/plans/2026-09-02-unified-room-solve-plan.md) is the
   *  room's edit token, once the caller has actually saved the room and
   *  knows it — omit it (as every existing caller does) and the body is
   *  `{}`, byte-identical to today, so the pre-existing signed-in
   *  one-click path is unaffected. */
  notifyJob: (jobId: string, roomToken?: string, traceId?: string) => Promise<{ ok?: boolean }>;
  /** Claim applying a `solve_room` job's result to its room, exactly once
   *  (§3.11 step 5). Always resolves 200 — `applied: false` covers every
   *  reason the caller didn't win the race (already applied elsewhere, job
   *  not owned/found); see backend/routers/jobs.py's admin_job_applied. */
  markJobApplied: (jobId: string, traceId?: string) => Promise<JobAppliedResponse>;
  /** `origin` (job pipeline W3, §3.1) on each row lets a tab reattach only
   *  to its OWN jobs (`origin.tabId === tabId()`) and a Design find results
   *  computed for it by another tab (`origin.designId`) — see
   *  `activeJob.ts`'s reattach gating. `null`/absent on a row means the job
   *  predates the origin contract, or was submitted through a door that
   *  doesn't send one (e.g. `submitOnboardingSolve`). */
  listActiveJobs: (traceId?: string) => Promise<{
    jobs: Array<{
      id: string;
      status: string;
      kind: string;
      resume_count?: number;
      origin?: JobOrigin | null;
    }>;
  }>;
  createProduct: (body: Partial<AdminProduct>) => Promise<AdminProduct>;
  updateProduct: (id: string, body: Partial<AdminProduct>) => Promise<AdminProduct>;
  deleteProduct: (id: string) => Promise<{ ok?: boolean }>;
}

const DEFAULT_TRACE_HEADER = 'X-Trace-Id';

const TRACED_PATHS = new Set<string>([
  '/api/admin/optimize/listener',
  '/api/admin/optimize/speakers',
  '/api/admin/optimize/joint',
  '/api/admin/jobs',
  '/api/admin/freq_response',
  '/api/admin/soundstage',
  '/api/onboarding/placement',
]);

function shouldAutoTrace(path: string): boolean {
  if (TRACED_PATHS.has(path)) return true;
  return path.startsWith('/api/admin/jobs/');
}

function pipelineHeaders(pipeline?: ModalPipeline): Record<string, string> {
  if (pipeline === 'analytical') return { 'X-Modal-Pipeline': 'analytical' };
  return { 'X-Modal-Pipeline': 'voxel' };
}

function joinUrl(baseUrl: string | undefined, path: string): string {
  if (!baseUrl) return path;
  const base = baseUrl.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

function defaultTraceId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '').slice(0, 8);
    }
  } catch {
    /* fall through */
  }
  return Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
}

/** Tiny capacity-bounded LRU. `fetchArtifact`'s cache: a sha is immutable
 *  by construction, so there is no invalidation to worry about — only
 *  eviction. `Map` iteration order is insertion order, so re-inserting on
 *  every hit (delete + set) keeps the least-recently-used entry first. */
class LruMap<K, V> {
  private map = new Map<K, V>();
  constructor(private maxEntries: number) {}
  get(key: K): V | undefined {
    const value = this.map.get(key);
    if (value === undefined) return undefined;
    this.map.delete(key);
    this.map.set(key, value);
    return value;
  }
  set(key: K, value: V): void {
    this.map.delete(key);
    this.map.set(key, value);
    if (this.map.size > this.maxEntries) {
      const oldest = this.map.keys().next().value;
      if (oldest !== undefined) this.map.delete(oldest);
    }
  }
}

const ARTIFACT_CACHE_MAX_ENTRIES = 8;

export function createAdminApi(options: AdminApiOptions = {}): AdminApi {
  const fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  const credentials = options.credentials;
  const getExtraHeaders = options.getExtraHeaders ?? (() => ({}));
  const newTraceId = options.newTraceId ?? defaultTraceId;
  const onTrace = options.onTrace;
  const traceHeader = options.traceHeader ?? DEFAULT_TRACE_HEADER;
  const baseUrl = options.baseUrl;
  const artifactCache = new LruMap<string, Uint8Array>(ARTIFACT_CACHE_MAX_ENTRIES);
  const artifactInflight = new Map<string, Promise<Uint8Array>>();
  const queuedLane = createQueuedJobLane(
    options.maxConcurrentQueuedJobs ?? DEFAULT_MAX_CONCURRENT_QUEUED_JOBS,
  );

  async function fetchArtifact(sha: string, signal?: AbortSignal): Promise<Uint8Array> {
    const cached = artifactCache.get(sha);
    if (cached) return cached;
    const inflight = artifactInflight.get(sha);
    if (inflight) return inflight;
    const promise = (async () => {
      const path = `/api/artifacts/${encodeURIComponent(sha)}`;
      const res = await fetchImpl(joinUrl(baseUrl, path), {
        credentials,
        headers: { ...getExtraHeaders() },
        signal,
      });
      if (!res.ok) throw await adminErrorFromResponse(res, path);
      if (typeof DecompressionStream === 'undefined') {
        throw new Error('DecompressionStream unavailable — cannot decode artifact');
      }
      if (!res.body) {
        throw new Error('artifact response had no body');
      }
      const decompressed = new Response(res.body.pipeThrough(new DecompressionStream('gzip')));
      const bytes = new Uint8Array(await decompressed.arrayBuffer());
      artifactCache.set(sha, bytes);
      return bytes;
    })();
    artifactInflight.set(sha, promise);
    try {
      return await promise;
    } finally {
      artifactInflight.delete(sha);
    }
  }

  const QUEUED_KINDS = new Set([
    'modal',
    'modal_volume',
    'modal_volume_sweep',
    'modal_velocity',
    'freq_response',
    'rt60',
    'raytrace',
    'soundstage',
    'speaker_position_sweep',
    'panel_absorption',
  ]);
  const QUEUE_POLL_DELAY_MS = 500;
  const MAX_SUBMIT_RETRIES = 5;
  const MAX_POLL_FAILURES = 45;
  const MAX_POLL_DURATION_MS = 30 * 60 * 1000;

  function splitScene<T extends Record<string, unknown>>(request: T): {
    scene: Scene;
    action: Record<string, unknown>;
  } {
    const {
      Lx,
      Ly,
      H,
      speakers,
      listener_pos,
      panels,
      furniture,
      windows,
      ...action
    } = request as T & {
      Lx: number;
      Ly: number;
      H: number;
      speakers?: Scene['speakers'];
      listener_pos: Scene['listener_pos'];
      panels?: Scene['panels'];
      furniture?: Scene['furniture'];
      windows?: Scene['windows'];
    };
    return {
      scene: {
        Lx,
        Ly,
        H,
        speakers: speakers ?? [],
        listener_pos,
        panels: panels ?? [],
        furniture: furniture ?? [],
        windows: windows ?? [],
        ...action,
      } as Scene,
      action,
    };
  }

  // `origin` is constructed ONCE by the caller and passed in unchanged —
  // every retry attempt below reuses the exact same object, so its
  // `clientRequestId` never changes across retries. That reuse IS the
  // idempotency promise (job pipeline W3, §3.1): the server treats
  // `(owner_key, clientRequestId)` as one job, so a retried submit that
  // actually landed server-side (the response was merely lost) comes back
  // as the SAME job id (`deduped`) instead of spawning a duplicate solve.
  //
  // `waitOnOwnerBusy`: an `owner_busy` 429 means "your other jobs are still
  // running", not "no". A queued chart job waits it out (honouring
  // Retry-After) -- the lane keeps this client at the cap, but a solve
  // submitted through `submitJob` holds a server slot the lane cannot see.
  // `submitJob` itself keeps surfacing it: useJobRun shows the user an
  // honest "several solves in progress" error for that case.
  async function submitWithRetry(
    scene: Scene,
    action: Record<string, unknown>,
    pipeline?: ModalPipeline,
    traceId?: string,
    origin?: JobOrigin,
    waitOnOwnerBusy = false,
  ): Promise<string> {
    let attempt = 0;
    while (true) {
      try {
        const submitted = await post<JobSubmitRequest, JobSubmitResponse>(
          '/api/admin/jobs',
          { scene, action: action as JobAction, ...(origin ? { origin } : {}) },
          pipeline,
          traceId,
        );
        return submitted.job_id;
      } catch (error) {
        const retryable = error instanceof AdminApiError
          ? error.status >= 500
            || (error.status === 429
              && (error.errorCode !== 'owner_busy' || waitOnOwnerBusy)
              && error.errorCode !== 'rate_limited'
              && error.errorCode !== 'simulator_daily_limit')
          : true;
        if (!retryable || attempt >= MAX_SUBMIT_RETRIES - 1) throw error;
        const retryAfter = error instanceof AdminApiError
          ? error.retryAfter
          : undefined;
        const delayMs = retryAfter !== undefined
          ? Math.max(250, retryAfter * 1000)
          : Math.min(5000, 250 * 2 ** attempt);
        attempt += 1;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }

  async function runQueued<T>(
    scene: Scene,
    action: Record<string, unknown>,
    pipeline?: ModalPipeline,
    explicitTraceId?: string,
    origin?: JobOrigin,
  ): Promise<T> {
    // The slot covers the job's whole server-side life (active = pending or
    // running), which is what the per-owner cap counts.
    const release = await queuedLane.acquire();
    try {
      return await runQueuedInLane<T>(scene, action, pipeline, explicitTraceId, origin);
    } finally {
      release();
    }
  }

  async function runQueuedInLane<T>(
    scene: Scene,
    action: Record<string, unknown>,
    pipeline?: ModalPipeline,
    explicitTraceId?: string,
    origin?: JobOrigin,
  ): Promise<T> {
    const traceId = explicitTraceId ?? newTraceId();
    const jobId = await submitWithRetry(scene, action, pipeline, traceId, origin, true);
    const deadline = Date.now() + MAX_POLL_DURATION_MS;
    let pollFailures = 0;
    while (true) {
      if (Date.now() >= deadline) {
        throw new Error('Solver did not finish within the allowed time. Please retry.');
      }
      let status: JobPollResponse;
      try {
        status = await get<JobPollResponse>(
          `/api/admin/jobs/${encodeURIComponent(jobId)}`,
          traceId,
          15000,
        );
      } catch (error) {
        pollFailures += 1;
        const retryable = error instanceof AdminApiError
          ? error.status === 429 || error.status >= 500
          : true;
        if (!retryable || pollFailures >= MAX_POLL_FAILURES) throw error;
        const retryAfter = error instanceof AdminApiError
          ? error.retryAfter
          : undefined;
        await new Promise((resolve) => setTimeout(
          resolve,
          retryAfter !== undefined
            ? Math.max(250, retryAfter * 1000)
            : QUEUE_POLL_DELAY_MS,
        ));
        continue;
      }
      pollFailures = 0;
      if (status.status === 'done') {
        // The done poll carries the owner's post-charge `eval_remaining`
        // (backend/routers/jobs.py poll_optimize). Composite kinds such as
        // `analyze_scene` do not embed it in their own result, so surface it
        // the same way `evaluate` always did: merged onto the result object
        // when the result is a plain object that does not already carry it.
        const result = status.result;
        if (
          status.eval_remaining !== undefined
          && result !== null && typeof result === 'object' && !Array.isArray(result)
          && !('eval_remaining' in (result as Record<string, unknown>))
        ) {
          return { ...(result as Record<string, unknown>), eval_remaining: status.eval_remaining } as T;
        }
        return result as T;
      }
      if (status.status === 'error') {
        throw new AdminApiError(
          status.error_code === 'resource_limit' ? 422 : 500,
          `/api/admin/jobs/${encodeURIComponent(jobId)}`,
          status.error,
          status.error_code,
        );
      }
      await new Promise((resolve) => setTimeout(resolve, QUEUE_POLL_DELAY_MS));
    }
  }

  function queuedRequest<T extends Record<string, unknown>>(
    kind: string,
    request: T,
  ): { scene: Scene; action: Record<string, unknown> } {
    if (!QUEUED_KINDS.has(kind)) {
      throw new Error(`Unsupported queued physics kind: ${kind}`);
    }
    const { scene, action } = splitScene(request);
    return { scene, action: { kind, ...action } };
  }

  async function post<TReq, TRes>(
    path: string,
    body: TReq,
    pipeline?: ModalPipeline,
    explicitTraceId?: string,
  ): Promise<TRes> {
    const traceId = explicitTraceId
      ?? (shouldAutoTrace(path) ? newTraceId() : null);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...pipelineHeaders(pipeline),
      ...getExtraHeaders(),
    };
    if (traceId) {
      headers[traceHeader] = traceId;
      onTrace?.(traceId, 'fe.adminApi.post.start', { path, pipeline: pipeline ?? 'voxel' });
    }
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    let res: Response;
    try {
      res = await fetchImpl(joinUrl(baseUrl, path), {
        method: 'POST',
        credentials,
        headers,
        body: JSON.stringify(body),
      });
    } catch (err) {
      if (traceId) {
        onTrace?.(traceId, 'fe.adminApi.post.network_error', {
          path,
          t_ms: typeof performance !== 'undefined' ? performance.now() - t0 : 0,
          error: err instanceof Error ? err.name : 'unknown',
        });
      }
      throw err;
    }
    if (traceId) {
      onTrace?.(traceId, 'fe.adminApi.post.response', {
        path,
        status: res.status,
        t_ms: typeof performance !== 'undefined' ? performance.now() - t0 : 0,
      });
    }
    if (!res.ok) throw await adminErrorFromResponse(res, path);
    const tParseStart = typeof performance !== 'undefined' ? performance.now() : 0;
    const parsed = (await res.json()) as TRes;
    if (traceId) {
      onTrace?.(traceId, 'fe.adminApi.post.parsed', {
        path,
        t_ms: typeof performance !== 'undefined' ? performance.now() - t0 : 0,
        t_parse_ms: typeof performance !== 'undefined' ? performance.now() - tParseStart : 0,
      });
    }
    return parsed;
  }

  async function get<TRes>(
    path: string,
    explicitTraceId?: string,
    timeoutMs?: number,
  ): Promise<TRes> {
    const traceId = explicitTraceId
      ?? (shouldAutoTrace(path) ? newTraceId() : null);
    const headers: Record<string, string> = { ...getExtraHeaders() };
    if (traceId) {
      headers[traceHeader] = traceId;
      onTrace?.(traceId, 'fe.adminApi.get.start', { path });
    }
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    const ctrl = timeoutMs ? new AbortController() : null;
    const abortTimer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
    let res: Response;
    try {
      res = await fetchImpl(joinUrl(baseUrl, path), {
        credentials,
        headers,
        signal: ctrl?.signal,
      });
    } finally {
      if (abortTimer) clearTimeout(abortTimer);
    }
    if (traceId) {
      onTrace?.(traceId, 'fe.adminApi.get.response', {
        path,
        status: res.status,
        t_ms: typeof performance !== 'undefined' ? performance.now() - t0 : 0,
      });
    }
    if (!res.ok) throw await adminErrorFromResponse(res, path);
    return res.json() as Promise<TRes>;
  }

  async function put<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
    const res = await fetchImpl(joinUrl(baseUrl, path), {
      method: 'PUT',
      credentials,
      headers: {
        'Content-Type': 'application/json',
        ...getExtraHeaders(),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await adminErrorFromResponse(res, path);
    return res.json() as Promise<TRes>;
  }

  async function del<TRes>(path: string): Promise<TRes> {
    const res = await fetchImpl(joinUrl(baseUrl, path), {
      method: 'DELETE',
      credentials,
      headers: { ...getExtraHeaders() },
    });
    if (!res.ok) throw await adminErrorFromResponse(res, path);
    return res.json() as Promise<TRes>;
  }

  return {
    materials: async () =>
      normalizeMaterialsResponse(
        await get<Record<string, unknown>>('/api/admin/materials'),
      ),
    products: () => get<{ products: AdminProduct[] } | AdminProduct[]>('/api/admin/products'),

    freqResponse: (req, pipeline) => {
      const queued = queuedRequest('freq_response', req as unknown as Record<string, unknown>);
      return runQueued<FreqResponseResponse>(queued.scene, queued.action, pipeline);
    },

    rt60: (req, pipeline) => {
      const queued = queuedRequest('rt60', req as unknown as Record<string, unknown>);
      return runQueued<RT60Response>(queued.scene, queued.action, pipeline);
    },

    modal: (req, pipeline) => {
      const queued = queuedRequest('modal', req as unknown as Record<string, unknown>);
      return runQueued<ModalResponse>(queued.scene, queued.action, pipeline);
    },

    modalVolume: (req, pipeline) => {
      const queued = queuedRequest('modal_volume', req as unknown as Record<string, unknown>);
      return runQueued<ModalVolumeResponse>(queued.scene, queued.action, pipeline);
    },

    modalVolumeSweep: (req, pipeline) => {
      const queued = queuedRequest(
        'modal_volume_sweep',
        req as unknown as Record<string, unknown>,
      );
      return runQueued<ModalVolumeSweepResponse>(queued.scene, queued.action, pipeline);
    },

    modalVelocity: (req, pipeline) => {
      const queued = queuedRequest(
        'modal_velocity',
        req as unknown as Record<string, unknown>,
      );
      return runQueued<ModalVelocityResponse>(queued.scene, queued.action, pipeline);
    },

    raytrace: (req) => {
      const queued = queuedRequest('raytrace', req as unknown as Record<string, unknown>);
      return runQueued<RaytraceResponse>(queued.scene, queued.action);
    },

    panelAbsorption: (req) => {
      const queued = queuedRequest(
        'panel_absorption',
        req as unknown as Record<string, unknown>,
      );
      return runQueued<PanelAbsorptionResponse>(queued.scene, queued.action);
    },

    porousAbsorber: (req) =>
      post<PorousAbsorberRequest, PorousAbsorberResponse>('/api/admin/porous_absorber', req),

    speakerSweep: (req) => {
      const queued = queuedRequest(
        'speaker_position_sweep',
        req as unknown as Record<string, unknown>,
      );
      return runQueued<AdminSpeakerSweepResponse>(queued.scene, queued.action);
    },

    soundstage: (req) => {
      const queued = queuedRequest('soundstage', req as unknown as Record<string, unknown>);
      return runQueued<SoundstageResponse>(queued.scene, queued.action);
    },

    optimizeListener: (req, pipeline, traceId) =>
      post<OptimizeListenerRequest, OptimizeResponse>(
        '/api/admin/optimize/listener', req, pipeline, traceId),

    optimizeSpeakers: (req, pipeline, traceId) =>
      post<OptimizeSpeakersRequest, OptimizeResponse>(
        '/api/admin/optimize/speakers', req, pipeline, traceId),

    optimizeJoint: (req, pipeline, traceId) =>
      post<OptimizeJointRequest, OptimizeResponse>(
        '/api/admin/optimize/joint', req, pipeline, traceId),

    optimizeRoomTreatment: (req, pipeline, traceId) =>
      post<RoomTreatmentRequest, RoomTreatmentResponse>(
        '/api/admin/optimize/room_treatment', req, pipeline, traceId),

    evaluate: (req) => {
      const { scene } = splitScene(
        req.freq_response as unknown as Record<string, unknown>,
      );
      return runQueued<AdminEvaluateResponse>(
        scene,
        {
          kind: 'evaluate',
          request_id: req.request_id,
          freq_response: req.freq_response,
          rt60: req.rt60,
          soundstage: req.soundstage,
        },
      );
    },

    analyzeScene: (req, origin) => {
      const { scene } = splitScene(
        req.freq_response as unknown as Record<string, unknown>,
      );
      return runQueued<AnalyzeSceneResponse>(
        scene,
        {
          kind: 'analyze_scene',
          request_id: req.request_id,
          freq_response: req.freq_response,
          rt60: req.rt60,
          soundstage: req.soundstage,
          viz: req.viz,
        },
        undefined,
        undefined,
        origin,
      );
    },

    fetchArtifact,

    submitJob: async (scene, action, pipeline, traceId, origin) => ({
      job_id: await submitWithRetry(
        scene, action as unknown as Record<string, unknown>, pipeline, traceId, origin,
      ),
    }),

    // One-shot POST, deliberately no submitWithRetry: a 409 (freebie already
    // spent) is not retryable and must reach the caller so it can fall back
    // to submitJob — retrying it here would just relearn 409 five times.
    submitOnboardingSolve: (scene, action, pipeline, traceId) =>
      post<JobSubmitRequest, JobSubmitResponse>(
        '/api/onboarding/placement',
        { scene, action },
        pipeline,
        traceId,
      ),

    pollJob: (jobId, traceId, timeoutMs) =>
      get<JobPollResponse>(`/api/admin/jobs/${encodeURIComponent(jobId)}`, traceId, timeoutMs),

    cancelJob: (jobId, traceId) =>
      post<Record<string, never>, { ok?: boolean }>(
        `/api/admin/jobs/${encodeURIComponent(jobId)}/cancel`, {}, undefined, traceId),

    notifyJob: (jobId, roomToken, traceId) =>
      post<{ room_token?: string }, { ok?: boolean }>(
        `/api/admin/jobs/${encodeURIComponent(jobId)}/notify`,
        roomToken ? { room_token: roomToken } : {},
        undefined,
        traceId,
      ),

    markJobApplied: (jobId, traceId) =>
      post<Record<string, never>, JobAppliedResponse>(
        `/api/admin/jobs/${encodeURIComponent(jobId)}/applied`, {}, undefined, traceId),

    listActiveJobs: (traceId) =>
      get<{
        jobs: Array<{
          id: string;
          status: string;
          kind: string;
          resume_count?: number;
          origin?: JobOrigin | null;
        }>;
      }>('/api/admin/jobs/active', traceId),

    createProduct: (body) =>
      post<Partial<AdminProduct>, AdminProduct>('/api/admin/products', body),

    updateProduct: (id, body) =>
      put<Partial<AdminProduct>, AdminProduct>(`/api/admin/products/${encodeURIComponent(id)}`, body),

    deleteProduct: (id) =>
      del<{ ok?: boolean }>(`/api/admin/products/${encodeURIComponent(id)}`),
  };
}
